import React, { useContext } from 'react';
import JobInformation from '../jobInformation';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useMutation, useQueryClient } from 'react-query';
import { WelletContext } from '@/context/wellet/welletContext';
import { putUserDocumentation } from '@/services';
import { useTranslation } from 'react-i18next';
import EvidenceCard from '../evidenceCard';
import { Show } from '@/components';
import { Toast } from '@/uiComponents/common';

const ProfessionalDocumentation = ({
  mode,
  affiliateType,
  id,
  profilePic,
  formRef,
  userQuery,
  profilePicBase64,
  setIsSaving,
}) => {
  const { t, i18n } = useTranslation('translation', {
    keyPrefix: 'screens.profile',
  });
  const welletContext = useContext(WelletContext);
  const organization = useSelector((state) => state.app.selectedOrganization);
  const queryClient = useQueryClient();

  const resetData = (data) => {
    const body = {
      sid: data?.sid,
      email: data?.email,
      phone: data?.phone,
      status: data?.isActive ? 'ACTIVE' : 'INACTIVE',
      sector: data?.sector,
      jobTitle: data?.jobTitle,
      company: data?.company,
      companyId: data?.organizationId,
      roles: data?.roles?.map((role) => role) ?? [],
      internRef: data?.internRef,
      profilePic: profilePicBase64 ? profilePicBase64 : null,
      hasFamiliar: data?.hasFamilyWorking ? 'false' : 'true',
      hasWorked: data?.hasWorkedPreviously ? 'false' : 'true',
      corporateEmail: data?.corporateEmail,
      webCompany: data?.webCompany,
      socialNetworkCompany: data?.socialNetworkCompany,
      officeAddressCompany: data?.officeAddressCompany,
      notes: data?.notes,
      workCredentialImage: data?.workCredentialImage ? data?.workCredentialImage : null,
      affiliateType: data?.affiliateType,
      workCredentialStatus: data?.workCredentialStatus ? data?.workCredentialStatus : null,
      workUnit: data?.workUnit ? data?.workUnit : null,
      categoryId: data?.salesCategory ? data?.salesCategory?.id : null,
    };
    return body;
  };

  const methods = useForm({
    defaultValues: resetData(userQuery?.data?.data),
  });

  const refetchStatus = () => {
    userQuery.refetch().then(() => profilePic.refetch());
    queryClient.refetchQueries({ queryKey: ['count-sidebar', organization.marketId] });
  };

  const onSubmit = (data) => {
    const body = {
      userId: data?.sid,
      sector: data?.sector,
      jobTitle: data?.jobTitle,
      company: data?.company,
      internRef: data.internRef,
      corporateEmail: data?.corporateEmail,
      webCompany: data?.webCompany,
      socialNetworkCompany: data?.socialNetworkCompany,
      officeAddressCompany: data?.officeAddressCompany,
      affiliateType: data?.affiliateType,
      workUnit: data?.workUnit,
    };
    if (!id) {
      body['password'] = data.password;
    }
    userMutation.mutate(body);
  };

  const userMutation = useMutation({
    mutationFn: (body) => {
      return putUserDocumentation(welletContext, organization.marketId, body);
    },
    onMutate: () => {
      setIsSaving(true);
    },
    onSettled: () => {
      setIsSaving(false);
    },
  });

  return (
    <FormProvider {...methods}>
      <form key={'user-doc'} onSubmit={methods.handleSubmit(onSubmit)}>
        <button ref={formRef} type='submit' style={{ display: 'none' }} />
        <div className='tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-6'>
          <div className='tw-flex tw-flex-col tw-col-span-3  md:tw-col-span-2 '>
            <JobInformation
              t={t}
              className={'tw-mb-6'}
              mode={mode}
              id={id}
              refetchStatus={refetchStatus}
              affiliateType={affiliateType}
            />
          </div>
          <div className='tw-flex tw-flex-col tw-col-span-3  md:tw-col-span-1 '>
            <EvidenceCard
              t={t}
              i18n={i18n}
              className={'tw-mb-6'}
              mode={mode}
              id={id}
              refetchStatus={refetchStatus}
              affiliateType={affiliateType}
            />
          </div>
        </div>
      </form>
      <Show.When isTrue={userMutation.isSuccess}>
        <Toast
          message={t('documentationProfessional.toast.success')}
          type={`SUCCESS`}
          onCallback={() => {
            setIsSaving(false);
            userMutation.reset();
            userQuery.refetch();
          }}
        />
      </Show.When>
      <Show.When isTrue={userMutation.isError}>
        <Toast message={t('toast.error')} type={`ERROR`} onCallback={() => userMutation.reset()} />
      </Show.When>
    </FormProvider>
  );
};

export default ProfessionalDocumentation;
